import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { createContext, FC, useContext, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetCountryListForDropdowns } from "../../api/services/client/clientApis";
import {
  SearchGenericParticipantsParams,
  SearchNISParticipantsParams,
  SearchProgramCommonParams,
  SearchProgramIndividualParams,
  SearchProgramParams,
} from "../../api/services/program/models";
import {
  useSearchGenericAccountParticipantsMutation,
  useSearchNISParticipantsMutation,
  useSearchProgramMutation,
} from "../../api/services/program/programApis";
import {
  useGetLineOfBusiness,
  useGetSolutionCategory,
  useGetSolutionOffering,
} from "../../api/services/programTemplate/programTemplateApis";
import { GetUsersDetailByRoleParams } from "../../api/services/user/models";
import { useGetUsersDetailByRoleMutation } from "../../api/services/user/userApi";
import { InformationModal } from "../../components";
import {
  BE_ERROR_CODES,
  LANGUAGE_CODE,
  PAGE_IDS,
  ROLE_IDS,
} from "../../helpers/constants";
import { getDateFormatByCountry } from "../../helpers/constants/countryDateFormat";
import {
  convertToUTCFormat,
  getOperatorFilterObject,
  getServiceOfficeCountryCodes,
  isBETimedOutError,
  isErrorCode,
  isNotEmpty,
  trimValue,
} from "../../helpers/utils";
import { useEnterKeyAction } from "../../hooks/useEnterKeyAction";
import { useSiteCore } from "../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import {
  updateProgramCreatedDate,
  updateProgramStatus,
} from "../../redux/slices/program/programSlice";
import { programTemplateSiteCoreKeys } from "../programTemplateSearch/siteCore/SiteCoreKeys";
import { ProgramSearchWrapper } from "./ProgramSearch.style";
import { GenericParticipantSearchResults } from "./components/genericParticipantSearchResults/GenericParticipantSearchResults";
import { NISParticipantSearchResult } from "./components/nisParticipantSearchResults/NISParticipantSearchResult";
import { ProgramSearchFormMain } from "./components/programSearchFormMain/ProgramSearchFormMain";
import { ProgramSearchHeader } from "./components/programSearchHeader/ProgramSearchHeader";
import { ProgramSearchResult } from "./components/programSearchResult/ProgramSearchResult";
import { SearchProgramSchema } from "./modelsAndSchemas/ValidationSchema";
import {
  ProgramGenericAccountSearchParticipantsResultRowValue,
  ProgramNISParticipantsSearchResultRowValue,
  ProgramSearchData,
  ProgramSearchDataProviderProps,
  ProgramSearchProgramFormKey,
  ProgramSearchProgramFormProps,
  ProgramSearchResultRowValue,
  searchTypeKey,
} from "./modelsAndSchemas/models";

dayjs.extend(utc);

const ProgramSearchDataContext = createContext<ProgramSearchData | undefined>(
  undefined,
);
export const useProgramSearchData = (): ProgramSearchData => {
  const context = useContext(ProgramSearchDataContext);
  if (!context) {
    throw new Error(
      "useProgramSearchData must be used within a ProgramSearchDataProvider",
    );
  }
  return context;
};

export const ProgramSearchDataProvider: React.FC<
  ProgramSearchDataProviderProps
> = ({ children, apiData }) => {
  return (
    <ProgramSearchDataContext.Provider value={apiData}>
      {children}
    </ProgramSearchDataContext.Provider>
  );
};

const pageId = PAGE_IDS.SEARCH_PROGRAM;

export const programSearchProgramDefaultValues = {
  [ProgramSearchProgramFormKey.searchType]: searchTypeKey.individuals,
  [ProgramSearchProgramFormKey.participantFirstName]: "",
  [ProgramSearchProgramFormKey.participantFirstNameFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.participantLastName]: "",
  [ProgramSearchProgramFormKey.participantLastNameFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.keyNumber]: "",
  [ProgramSearchProgramFormKey.keyNumberFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.participantEmail]: "",
  [ProgramSearchProgramFormKey.participantEmailFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.isPrimaryParticipantEmail]: false, // Default value is kept false to keep toggle OFF but should be passed as true as its primary value by default
  [ProgramSearchProgramFormKey.nextParticipantEmail]: "",
  [ProgramSearchProgramFormKey.nextParticipantEmailFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.isPrimaryNEXTParticipantEmail]: false, // Default value is kept false to keep toggle OFF but should be passed as true as its primary value by default
  [ProgramSearchProgramFormKey.mobileISOCode]: "",
  [ProgramSearchProgramFormKey.mobileCountryCode]: "",
  [ProgramSearchProgramFormKey.participantMobileNumber]: "",
  [ProgramSearchProgramFormKey.isPrimaryParticipantMobileNumber]: false, // Default value is kept false to keep toggle OFF but should be passed as true as its primary value by default
  [ProgramSearchProgramFormKey.clientName]: "",
  [ProgramSearchProgramFormKey.clientNameFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.clientManagedStatus]: "",
  [ProgramSearchProgramFormKey.billingContactLastName]: "",
  [ProgramSearchProgramFormKey.billingContactLastNameFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.projectKey]: "",
  [ProgramSearchProgramFormKey.projectKeyFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.projectName]: "",
  [ProgramSearchProgramFormKey.projectNameFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.hrContactLastName]: "",
  [ProgramSearchProgramFormKey.hrContactLastNameFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.programStatus]: "",
  [ProgramSearchProgramFormKey.programName]: "",
  [ProgramSearchProgramFormKey.programNameFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.programStartMonthAndYear]: "",
  [ProgramSearchProgramFormKey.projectManager]: "",
  [ProgramSearchProgramFormKey.lineOfBusiness]: "",
  [ProgramSearchProgramFormKey.solutionCategory]: "",
  [ProgramSearchProgramFormKey.clientRelationshipOwner]: "",
  [ProgramSearchProgramFormKey.genericAccountName]: "",
  [ProgramSearchProgramFormKey.genericAccountNameFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.genericRegistrationCode]: "",
  [ProgramSearchProgramFormKey.genericRegistrationCodeFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.nextView]: "",
  [ProgramSearchProgramFormKey.genericAccountStatus]: "",
  [ProgramSearchProgramFormKey.nextProgramClass]: "",
  [ProgramSearchProgramFormKey.participantStatus]: "",
  [ProgramSearchProgramFormKey.purpose]: "",
  [ProgramSearchProgramFormKey.country]: "",
  [ProgramSearchProgramFormKey.deliveryOffice]: "",
  [ProgramSearchProgramFormKey.requestedBy]: "",
  [ProgramSearchProgramFormKey.nisId]: "",
  [ProgramSearchProgramFormKey.nisIdFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.clientEmployer]: "",
  [ProgramSearchProgramFormKey.clientEmployerFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.nisStatus]: "",
  [ProgramSearchProgramFormKey.hrContact]: "",
  [ProgramSearchProgramFormKey.hrContactFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.participantEmailPersonal]: "",
  [ProgramSearchProgramFormKey.participantEmailPersonalFilter]:
    programTemplateSiteCoreKeys.contains,
  [ProgramSearchProgramFormKey.isParticipantEmailPersonal]: false, // Default value is kept false to keep toggle OFF but should be passed as true as its primary value by default
  [ProgramSearchProgramFormKey.nisMobileISOCode]: "",
  [ProgramSearchProgramFormKey.nisMobileCountryCode]: "",
  [ProgramSearchProgramFormKey.nisParticipantMobileNumber]: "",
};

const ProgramSearch: FC = () => {
  const dispatch = useDispatch();
  const { fetchValidationMessage, commonLabels, commonValidationMessages } =
    useSiteCore();
  const [isSearchClick, setIsSearchClick] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const { loggedInUser } = useGetLoggedInUserDetails();

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [searchResultList, setSearchResultList] = useState<
    | ProgramSearchResultRowValue[]
    | ProgramGenericAccountSearchParticipantsResultRowValue[]
    | ProgramNISParticipantsSearchResultRowValue[]
  >([]);

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const countryCodeListString = getServiceOfficeCountryCodes(loggedInUser);

  const methods = useForm<ProgramSearchProgramFormProps>({
    mode: "all",
    defaultValues: programSearchProgramDefaultValues,
    resolver: yupResolver(
      SearchProgramSchema(fetchValidationMessage, commonValidationMessages),
    ),
  });
  const {
    getValues,
    control,
    formState: { isValid },
    handleSubmit,
  } = methods;

  const currentSearchType = getValues(ProgramSearchProgramFormKey.searchType);

  const formValues = useWatch({ control });
  // While disabling the save btn - empty form - Fields to ignore in the check
  const ignoredFields = [
    ProgramSearchProgramFormKey.searchType,
    ProgramSearchProgramFormKey.resetForm,
    ProgramSearchProgramFormKey.hrContactLastNameFilter,
    ProgramSearchProgramFormKey.keyNumberFilter,
    ProgramSearchProgramFormKey.billingContactLastNameFilter,
    ProgramSearchProgramFormKey.participantLastNameFilter,
    ProgramSearchProgramFormKey.participantFirstNameFilter,
    ProgramSearchProgramFormKey.participantEmailFilter,
    ProgramSearchProgramFormKey.nextParticipantEmailFilter,
    ProgramSearchProgramFormKey.clientNameFilter,
    ProgramSearchProgramFormKey.genericAccountNameFilter,
    ProgramSearchProgramFormKey.genericRegistrationCodeFilter,
    ProgramSearchProgramFormKey.programNameFilter,
    ProgramSearchProgramFormKey.projectKeyFilter,
    ProgramSearchProgramFormKey.projectNameFilter,
    ProgramSearchProgramFormKey.nisIdFilter,
    ProgramSearchProgramFormKey.clientEmployerFilter,
    ProgramSearchProgramFormKey.hrContactFilter,
    ProgramSearchProgramFormKey.participantEmailPersonalFilter,
  ] as string[];
  // While disabling the save btn - empty form - Check if any of the fields have values in them
  const isValidForm =
    isValid &&
    Object.entries(formValues).some(
      ([key, value]) =>
        !ignoredFields.includes(key) &&
        typeof value === "string" &&
        value.trim() !== "",
    );

  const {
    mutateAsync: getDedicatedProjectManagers,
    data: dedicatedProjectManagerData,
    isSuccess: isDedicatedProjectManagerApiSuccess,
    isError: isDedicatedProjectManagerApiError,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getClientRelationshipOwners,
    data: clientRelationshipOwnerData,
    isSuccess: isClientRelationshipOwnerApiSuccess,
    isError: isClientRelationshipOwnerApiError,
  } = useGetUsersDetailByRoleMutation();

  const { data: lineOfBusinessData, isError: isLineOfBusinessError } =
    useGetLineOfBusiness(LANGUAGE_CODE.EN_US);
  const {
    data: solutionCategories,
    isError: isSolutionCategoryError,
    refetchWithParams: refetchSolutionCategory,
  } = useGetSolutionCategory();

  const { data: solutionOfferings, isError: isSolutionOfferingError } =
    useGetSolutionOffering(LANGUAGE_CODE.EN_US, true);

  const { data: countryListData } = useGetCountryListForDropdowns();

  const {
    mutateAsync: getUsersDetailsByRole,
    data: requestedByList,
    isError: isUsersListError,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getProgramList,
    isPending,
    data: searchProgramResult,
    isError: isSearchProgramError,
    error: searchProgramError,
  } = useSearchProgramMutation();

  const {
    mutateAsync: getGenericAccountParticipantsList,
    isPending: isGenericAccountParticipantsPending,
    data: searchGenericAccountParticipantsResult,
    isError: isSearchGenericAccountParticipantsError,
    error: searchGenericAccountParticipantsError,
  } = useSearchGenericAccountParticipantsMutation();

  const {
    mutateAsync: getNISParticipantsList,
    isPending: isNISParticipantsPending,
    data: searchNISParticipantsResult,
    isError: isSearchNISParticipantsError,
    error: searchNISParticipantsError,
  } = useSearchNISParticipantsMutation();

  useEffect(() => {
    if (isNotEmpty(searchProgramResult)) {
      setSearchResultList(
        (searchProgramResult?.data as ProgramSearchResultRowValue[]) || [],
      );
    }
  }, [searchProgramResult]);

  useEffect(() => {
    if (isNotEmpty(searchGenericAccountParticipantsResult)) {
      setSearchResultList(
        (searchGenericAccountParticipantsResult?.data as ProgramGenericAccountSearchParticipantsResultRowValue[]) ||
          [],
      );
    }
  }, [searchGenericAccountParticipantsResult]);

  useEffect(() => {
    if (isNotEmpty(searchNISParticipantsResult)) {
      setSearchResultList(
        (searchNISParticipantsResult?.data as ProgramNISParticipantsSearchResultRowValue[]) ||
          [],
      );
    }
  }, [searchNISParticipantsResult]);

  useEffect(() => {
    if (
      isSearchProgramError ||
      isSearchGenericAccountParticipantsError ||
      isSearchNISParticipantsError
    ) {
      dispatch(toggleLoader({ isVisible: false }));
      if (
        isErrorCode(
          searchProgramError,
          BE_ERROR_CODES.SEARCH_PROGRAM_LIMIT_EXCEEDED,
        ) ||
        isErrorCode(
          searchNISParticipantsError,
          BE_ERROR_CODES.SEARCH_NIS_PARTICIPANT_LIMIT_EXCEEDED,
        ) ||
        isBETimedOutError(searchProgramError) ||
        isBETimedOutError(searchGenericAccountParticipantsError) ||
        isBETimedOutError(searchNISParticipantsError)
      ) {
        setShowErrorMessage(true);
        setIsSearchClick(false);
      }
    }
  }, [
    searchProgramError,
    isSearchProgramError,
    searchGenericAccountParticipantsError,
    isSearchGenericAccountParticipantsError,
    isSearchNISParticipantsError,
    searchNISParticipantsError,
  ]);

  const handleCloseErrorMessage = () => {
    setShowErrorMessage(false);
  };

  useEffect(() => {
    getUsersDetailsByRole({
      roleIds: [
        ROLE_IDS.sales,
        ROLE_IDS.admin,
        ROLE_IDS.corporateAdmin,
        ROLE_IDS.outreachConsultantLead,
        ROLE_IDS.projectManager,
        ROLE_IDS.projectCoOrdinator,
      ],
    });
    refetchSolutionCategory({ languageCode: LANGUAGE_CODE.EN_US });
    getClientRelationshipOwners({ roleIds: [ROLE_IDS.sales] });
  }, []);

  useEffect(() => {
    const countryCodeListString = getServiceOfficeCountryCodes(loggedInUser);
    if (isNotEmpty(countryCodeListString)) {
      const params: GetUsersDetailByRoleParams = {
        roleIds: [ROLE_IDS.projectManager],
        countryCode: countryCodeListString,
      };
      getDedicatedProjectManagers(params);
    }
  }, [loggedInUser?.role]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isSearchProgramPending: {
          isVisible:
            isPending ||
            isGenericAccountParticipantsPending ||
            isNISParticipantsPending,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isSearchProgramPending: {
            isVisible: false,
            message: "",
          },
        }),
      );
    };
  }, [
    isPending,
    isGenericAccountParticipantsPending,
    isNISParticipantsPending,
  ]);

  useEffect(() => {
    dispatch(updateProgramStatus(""));
    dispatch(updateProgramCreatedDate(undefined));
  }, []);

  const handleSearchClick = () => {
    setIsSearchClick(true);

    const params = buildSearchParams();
    if (!params) return;

    if (currentSearchType === searchTypeKey.nisParticipants) {
      getNISParticipantsList(params as SearchNISParticipantsParams);
    } else if (currentSearchType === searchTypeKey.genericParticipant) {
      getGenericAccountParticipantsList(
        params as SearchGenericParticipantsParams,
      );
    } else {
      // Only pass allowed types to getProgramList
      getProgramList(
        params as SearchProgramParams | SearchProgramIndividualParams,
      );
    }
  };

  const buildSearchParams = ():
    | SearchProgramParams
    | SearchProgramIndividualParams
    | SearchGenericParticipantsParams
    | SearchNISParticipantsParams
    | null => {
    const {
      participantFirstName,
      participantFirstNameFilter,
      participantLastName,
      participantLastNameFilter,
      keyNumber,
      keyNumberFilter,
      participantEmail: participantEmailValue,
      participantEmailFilter,
      isPrimaryParticipantEmail,
      nextParticipantEmail,
      nextParticipantEmailFilter,
      isPrimaryNEXTParticipantEmail,
      mobileISOCode,
      mobileCountryCode,
      participantMobileNumber,
      isPrimaryParticipantMobileNumber,
      clientName,
      clientNameFilter,
      clientManagedStatus,
      searchType,
      programName,
      programNameFilter,
      programStatus,
      programStartMonthAndYear,
      billingContactLastName,
      billingContactLastNameFilter,
      hrContactLastName,
      hrContactLastNameFilter,
      projectManager,
      lineOfBusiness,
      solutionCategory,
      clientRelationshipOwner,
      genericAccountName,
      genericAccountNameFilter,
      genericRegistrationCode,
      genericRegistrationCodeFilter,
      nextView,
      genericAccountStatus,
      nextProgramClass,
      participantStatus,
      purpose,
      country,
      deliveryOffice,
      requestedBy,
      projectKey,
      projectKeyFilter,
      projectName,
      projectNameFilter,
      nisId,
      nisIdFilter,
      clientEmployer,
      clientEmployerFilter,
      nisStatus,
      hrContact,
      hrContactFilter,
      participantEmailPersonal,
      participantEmailPersonalFilter,
      isParticipantEmailPersonal,
      nisMobileISOCode,
      nisMobileCountryCode,
      nisParticipantMobileNumber,
    } = getValues();

    const commonParams: SearchProgramCommonParams = {
      billingContactLastNameFilter,
      billingContactLastName: trimValue(billingContactLastName),
      hrContactLastNameFilter,
      hrContactLastName: trimValue(hrContactLastName),
      projectManagerId: projectManager,
      lineOfBusinessId: lineOfBusiness,
      solutionCategoryId: solutionCategory,
      clientRelationShipOwenerId: clientRelationshipOwner,
    };
    switch (searchType) {
      case searchTypeKey.individuals:
        return {
          participantFirstName,
          participantFirstNameFilter,
          participantLastName,
          participantLastNameFilter,
          keyNumber,
          keyNumberFilter,
          participantEmail: participantEmailValue,
          participantEmailFilter,
          ...(isNotEmpty(participantEmailValue) && {
            // Default value is kept false to keep toggle OFF but should be passed as true as its primary value by default
            isPrimaryParticipantEmail: !isPrimaryParticipantEmail,
          }),
          nextParticipantEmail,
          nextParticipantEmailFilter,
          ...(isNotEmpty(nextParticipantEmail) && {
            // Default value is kept false to keep toggle OFF but should be passed as true as its primary value by default
            isPrimaryNEXTParticipantEmail: !isPrimaryNEXTParticipantEmail,
          }),
          mobileISOCode,
          mobileCountryCode,
          participantMobileNumber,
          ...(isNotEmpty(participantMobileNumber) && {
            // Default value is kept false to keep toggle OFF but should be passed as true as its primary value by default
            isPrimaryParticipantMobileNumber: !isPrimaryParticipantMobileNumber,
          }),
          clientName,
          clientNameFilter,
          clientManagedStatus,
          programStatus,
          ...commonParams,
        };

      case searchTypeKey.programs:
        return {
          programName,
          programNameFilter,
          programStatus,
          projectManagerId: projectManager,
          lineOfBusinessId: lineOfBusiness,
          programDate: programStartMonthAndYear
            ? convertToUTCFormat(
                programStartMonthAndYear,
                userCountryDateFormat,
              )
            : "",
          projectKey,
          projectKeyFilter,
          projectName,
          projectNameFilter,
          billingContactLastNameFilter,
          billingContactLastName,
          hrContactLastNameFilter,
          hrContactLastName,
          clientRelationShipOwenerId: clientRelationshipOwner,
        };

      case searchTypeKey.genericParticipant:
        return {
          participantFirstName: getOperatorFilterObject(
            participantFirstName,
            participantFirstNameFilter,
          ),
          participantLastName: getOperatorFilterObject(
            participantLastName,
            participantLastNameFilter,
          ),
          genericAccountName: getOperatorFilterObject(
            genericAccountName,
            genericAccountNameFilter,
          ),
          genericAccountRegistrationCode: getOperatorFilterObject(
            genericRegistrationCode,
            genericRegistrationCodeFilter,
          ),
          participantEmail: getOperatorFilterObject(
            participantEmailValue,
            participantEmailFilter,
          ),
          clientName: getOperatorFilterObject(clientName, clientNameFilter),
          solutionOfferingId: nextView,
          genericAccountStatus,
          programClassId: nextProgramClass,
          participantStatus: participantStatus,
          mobileCountryCode,
          participantMobileNumber,
          purposeId: purpose,
          countryCode: isNotEmpty(country) ? country : null,
          deliveryOfficeId: deliveryOffice,
          requestedBy,
        };
      case searchTypeKey.nisParticipants:
        return {
          firstName: getOperatorFilterObject(
            participantFirstName,
            participantFirstNameFilter,
          ),
          lastName: getOperatorFilterObject(
            participantLastName,
            participantLastNameFilter,
          ),
          nisId: getOperatorFilterObject(nisId, nisIdFilter),
          clientName: getOperatorFilterObject(
            clientEmployer,
            clientEmployerFilter,
          ),
          email: getOperatorFilterObject(
            participantEmailPersonal,
            participantEmailPersonalFilter,
          ),
          ...(isNotEmpty(participantEmailPersonal) && {
            // Default value is kept false to keep toggle OFF but should be passed as true is its primary value by default
            isPersonalEmail: !isParticipantEmailPersonal,
          }),
          hrContact: getOperatorFilterObject(hrContact, hrContactFilter),
          nisStatus,
          mobileNumber: {
            countryCode: nisMobileCountryCode,
            isoCode: nisMobileISOCode,
            number: nisParticipantMobileNumber,
          },
          projectManagerId: projectManager,
          countryCode: isNotEmpty(country) ? country : null,
        } as SearchNISParticipantsParams;

      default:
        return null;
    }
  };

  // Prepare data for context
  const apiData = {
    solutionOfferingsData: solutionOfferings ?? [],
    countryListData: countryListData ?? [],
    requestedByListData: requestedByList ?? [],
    dedicatedProjectManagerData: dedicatedProjectManagerData ?? [],
    isDedicatedProjectManagerApiSuccess: isDedicatedProjectManagerApiSuccess,
    lineOfBusinessData: lineOfBusinessData ?? [],
    solutionCategories: solutionCategories ?? [],
    clientRelationshipOwnerData: clientRelationshipOwnerData ?? [],
    isClientRelationshipOwnerApiSuccess: isClientRelationshipOwnerApiSuccess,
    isValidForm,
    handleSearchClick,
    showMore,
    setShowMore,
    setSearchResultList,
    setIsSearchClick,
  };

  useEffect(() => {
    if (
      isDedicatedProjectManagerApiError ||
      isClientRelationshipOwnerApiError ||
      isLineOfBusinessError ||
      isSolutionCategoryError ||
      isSolutionOfferingError ||
      isUsersListError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [
    isDedicatedProjectManagerApiError,
    isClientRelationshipOwnerApiError,
    isLineOfBusinessError,
    isSolutionCategoryError,
    isSolutionOfferingError,
    isUsersListError,
  ]);

  useEnterKeyAction({
    onEnter: handleSubmit(() => handleSearchClick()),
  });

  return (
    <FormProvider {...methods}>
      <ProgramSearchWrapper data-testid="program-search">
        <ProgramSearchDataProvider apiData={apiData}>
          <ProgramSearchHeader />

          <ProgramSearchFormMain pageId={pageId} />
        </ProgramSearchDataProvider>

        {(currentSearchType === searchTypeKey.individuals ||
          currentSearchType === searchTypeKey.programs) && (
          <ProgramSearchResult
            rows={searchResultList as ProgramSearchResultRowValue[]}
            isSearch={isSearchClick}
            pageId={pageId}
            searchType={currentSearchType}
          />
        )}

        {currentSearchType === searchTypeKey.nisParticipants && (
          <NISParticipantSearchResult
            rows={
              searchResultList as ProgramNISParticipantsSearchResultRowValue[]
            }
            isSearch={isSearchClick}
            pageId={pageId}
            searchType={currentSearchType}
          />
        )}

        {currentSearchType === searchTypeKey.genericParticipant && (
          <GenericParticipantSearchResults
            rows={
              searchResultList as ProgramGenericAccountSearchParticipantsResultRowValue[]
            }
            isSearch={isSearchClick}
            pageId={pageId}
          />
        )}

        {showErrorMessage && (
          <InformationModal
            showModal={showErrorMessage}
            handleModalClose={handleCloseErrorMessage}
            handlePrimaryButtonClick={handleCloseErrorMessage}
            message={commonLabels.refineYourSearchInformationMessage}
          />
        )}
      </ProgramSearchWrapper>
    </FormProvider>
  );
};

export default ProgramSearch;
